import { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, List, ListItemButton, ListItemText, IconButton, CircularProgress, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { getMentionableUsers, uploadRemarkImage } from '../../api/logApi';
import handleAgentName from '../../utils/userUtils';

const PP = "'Poppins', sans-serif";

export default function MentionInput({ value, onChange, placeholder, disabled, module, recordId }) {
    const [users, setUsers] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [filter, setFilter] = useState('');
    const [mentionStart, setMentionStart] = useState(-1);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const textareaRef = useRef(null);
    const dropdownRef = useRef(null);
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    // Upload one or more image files, then inject ![photo](url) markdown into
    // the remark text at the cursor position. Failures are surfaced inline; the
    // textarea content otherwise stays untouched.
    const MAX_BYTES = 1 * 1024 * 1024;  // keep in sync with LogController.UploadRemarkImage
    const handleFilesPicked = useCallback(async (files) => {
        if (!files || files.length === 0) return;
        setUploadError('');
        setUploading(true);
        try {
            const inserts = [];
            for (const f of Array.from(files)) {
                if (f.size > MAX_BYTES) {
                    setUploadError(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(2)} MB — max is 1 MB. Compress and try again.`);
                    continue;
                }
                try {
                    const res = await uploadRemarkImage(f);
                    const url = res?.data?.url;
                    if (url) inserts.push(`![photo](${url})`);
                } catch (err) {
                    const msg = err?.response?.data || err?.message || 'Upload failed';
                    setUploadError(typeof msg === 'string' ? msg : 'Upload failed');
                }
            }
            if (inserts.length > 0) {
                const ta = textareaRef.current;
                const cur = ta ? (ta.selectionStart ?? value.length) : value.length;
                const snippet = (value && cur > 0 && !value.slice(cur - 1, cur).match(/\s/) ? ' ' : '') +
                                inserts.join(' ') + ' ';
                const next = value.slice(0, cur) + snippet + value.slice(cur);
                onChange(next);
                // Move cursor to after the inserted snippet
                setTimeout(() => {
                    if (ta) {
                        const pos = cur + snippet.length;
                        ta.focus();
                        ta.setSelectionRange(pos, pos);
                    }
                }, 0);
            }
        } finally {
            setUploading(false);
            // Reset the file input so picking the same file again re-fires onChange
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }, [value, onChange]);

    useEffect(() => {
        if (module && recordId) {
            getMentionableUsers(module, recordId)
                .then(res => setUsers(res.data || []))
                .catch(() => setUsers([]));
        }
    }, [module, recordId]);

    const getDisplayName = (u) => handleAgentName(u.firstName, u.lastName, u.alias) || u.username;

    const filtered = filter
        ? users.filter(u => {
            const q = filter.toLowerCase();
            return (u.username?.toLowerCase().includes(q))
                || (u.firstName?.toLowerCase().includes(q))
                || (u.lastName?.toLowerCase().includes(q))
                || (u.alias?.toLowerCase().includes(q));
        }).slice(0, 8)
        : users.slice(0, 8);

    const handleInput = useCallback((e) => {
        const text = e.target.value;
        const cursor = e.target.selectionStart;
        onChange(text);

        // Check if we're in a mention context
        const beforeCursor = text.slice(0, cursor);
        const lastAt = beforeCursor.lastIndexOf('@');

        if (lastAt >= 0) {
            // Check that @ is at start or preceded by whitespace
            const charBefore = lastAt > 0 ? text[lastAt - 1] : ' ';
            if (charBefore === ' ' || charBefore === '\n' || lastAt === 0) {
                const query = beforeCursor.slice(lastAt + 1);
                // Only show dropdown if no space in query (still typing the mention)
                if (!query.includes(' ')) {
                    setMentionStart(lastAt);
                    setFilter(query);
                    setShowDropdown(true);
                    setSelectedIndex(0);
                    return;
                }
            }
        }

        setShowDropdown(false);
    }, [onChange]);

    const insertMention = useCallback((user) => {
        const before = value.slice(0, mentionStart);
        // Find the end of current mention text
        const remaining = value.slice(mentionStart + 1);
        const spaceIdx = remaining.indexOf(' ');
        const endIdx = spaceIdx >= 0 ? mentionStart + 1 + spaceIdx : value.length;
        const afterMention = value.slice(endIdx);

        const newText = `${before}@${user.username} ${afterMention}`;
        onChange(newText);
        setShowDropdown(false);

        // Focus back on textarea
        setTimeout(() => {
            const pos = before.length + user.username.length + 2; // +2 for @ and space
            textareaRef.current?.focus();
            textareaRef.current?.setSelectionRange(pos, pos);
        }, 0);
    }, [value, mentionStart, onChange]);

    const handleKeyDown = (e) => {
        if (!showDropdown || filtered.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            if (showDropdown && filtered.length > 0) {
                e.preventDefault();
                insertMention(filtered[selectedIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                textareaRef.current && !textareaRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <Box sx={{ position: 'relative' }}>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                placeholder={placeholder || 'Enter remarks... Use @ to tag someone'}
                disabled={disabled}
                rows={5}
                style={{
                    width: '100%',
                    fontFamily: PP,
                    fontSize: '0.85rem',
                    padding: '10px 40px 10px 14px',  /* right pad so text doesn't run under the 📎 button */
                    border: '1px solid #C4C4C4',
                    borderRadius: '4px',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box',
                    lineHeight: 1.5,
                }}
                onFocus={(e) => e.target.style.borderColor = '#1A8CFF'}
                onBlur={(e) => e.target.style.borderColor = '#C4C4C4'}
            />

            {/* 📎 attach button — uploads image and inserts ![photo](url) markdown at cursor */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFilesPicked(e.target.files)}
            />
            <Tooltip title={uploading ? 'Uploading…' : 'Attach photo (jpg / png / gif / webp, max 1 MB)'} placement="top">
                <span style={{ position: 'absolute', top: 6, right: 6 }}>
                    <IconButton
                        size="small"
                        disabled={disabled || uploading}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ color: '#64748B', '&:hover': { color: '#1A8CFF', backgroundColor: '#EFF6FF' } }}
                    >
                        {uploading ? <CircularProgress size={16} thickness={5} /> : <AttachFileIcon fontSize="small" />}
                    </IconButton>
                </span>
            </Tooltip>
            {uploadError && (
                <Typography sx={{ fontSize: '0.72rem', color: '#DC2626', fontFamily: PP, mt: 0.5 }}>
                    {uploadError}
                </Typography>
            )}

            {showDropdown && filtered.length > 0 && (
                <Paper
                    ref={dropdownRef}
                    elevation={4}
                    sx={{
                        position: 'absolute',
                        bottom: '100%',
                        left: 0,
                        right: 0,
                        mb: 0.5,
                        maxHeight: 240,
                        overflow: 'auto',
                        zIndex: 1300,
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                    }}
                >
                    <List dense disablePadding>
                        {filtered.map((u, i) => (
                            <ListItemButton
                                key={u.userId}
                                selected={i === selectedIndex}
                                onClick={() => insertMention(u)}
                                sx={{
                                    py: 0.75,
                                    px: 1.5,
                                    '&.Mui-selected': { backgroundColor: '#EFF6FF' },
                                    '&:hover': { backgroundColor: '#F8FAFC' },
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography sx={{ fontSize: '0.82rem', fontFamily: PP, fontWeight: 500 }}>
                                            {getDisplayName(u)}
                                            <span style={{ color: '#94A3B8', fontWeight: 400, marginLeft: 8 }}>@{u.username}</span>
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>
            )}
        </Box>
    );
}

/**
 * Render remark text with @mentions highlighted AND ![alt](url) image markdown
 * rendered as inline thumbnails (click to open full-size in a new tab).
 * Split order matters: parse images first (multi-char tokens), then split each
 * remaining text chunk on @mentions.
 *
 * Static asset URLs (saved as relative paths like "/uploads/sales-remarks/...")
 * are resolved against the API host because the backend serves them from wwwroot —
 * the frontend dev server on localhost:3000 doesn't.
 */
const STATIC_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');
const resolveUrl = (u) => {
    if (!u) return u;
    if (/^https?:\/\//i.test(u)) return u;          // absolute, leave alone
    if (u.startsWith('/')) return `${STATIC_BASE}${u}`;
    return u;
};

/**
 * A bare URL inside a remark: rendered as a clickable link plus a small
 * copy-to-clipboard button (handy for sending a payment link on to a customer).
 */
function RemarkLink({ url }) {
    const [copied, setCopied] = useState(false);
    const copy = (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked — link is still clickable */ }
    };
    return (
        <span style={{ whiteSpace: 'normal' }}>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1A8CFF', textDecoration: 'underline', wordBreak: 'break-all' }}
            >
                {url}
            </a>
            <Tooltip title={copied ? 'Copied' : 'Copy link'} placement="top">
                <IconButton
                    size="small"
                    onClick={copy}
                    sx={{ p: '2px', ml: 0.25, verticalAlign: 'middle', color: copied ? '#16A34A' : '#94A3B8', '&:hover': { color: '#1A8CFF', backgroundColor: '#EFF6FF' } }}
                >
                    {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                </IconButton>
            </Tooltip>
        </span>
    );
}

export function RenderRemarks({ text }) {
    if (!text) return '—';

    // Tokenize: ![alt](url) image tags emerge as their own elements; everything
    // else flows through the @mention splitter below.
    const IMG_RE = /!\[[^\]]*\]\(([^)\s]+)\)/g;
    const tokens = [];
    let last = 0;
    let m;
    while ((m = IMG_RE.exec(text)) !== null) {
        if (m.index > last) tokens.push({ type: 'text', value: text.slice(last, m.index) });
        tokens.push({ type: 'img', url: m[1] });
        last = m.index + m[0].length;
    }
    if (last < text.length) tokens.push({ type: 'text', value: text.slice(last) });

    const renderTextChunk = (chunk, key) => {
        // Split on @mentions AND bare http(s) URLs so each becomes its own node.
        const parts = chunk.split(/(@\w+|https?:\/\/[^\s]+)/g);
        return parts.map((part, i) => {
            if (!part) return null;
            if (part.match(/^@\w+$/)) {
                return (
                    <span
                        key={`${key}-m${i}`}
                        style={{
                            color: '#1A8CFF',
                            fontWeight: 600,
                            backgroundColor: '#EFF6FF',
                            padding: '1px 4px',
                            borderRadius: '4px',
                            fontSize: '0.78rem',
                        }}
                    >
                        {part}
                    </span>
                );
            }
            if (/^https?:\/\//i.test(part)) {
                // Peel trailing punctuation (e.g. a period ending the sentence) off
                // the URL so it isn't swallowed into the link.
                const mt = part.match(/^(.*?)([.,;:!?)\]}]*)$/);
                const url = mt ? mt[1] : part;
                const tail = mt ? mt[2] : '';
                return (
                    <span key={`${key}-l${i}`}>
                        <RemarkLink url={url} />
                        {tail}
                    </span>
                );
            }
            return <span key={`${key}-t${i}`}>{part}</span>;
        });
    };

    return (
        <span>
            {tokens.map((tok, i) => {
                if (tok.type === 'img') {
                    const full = resolveUrl(tok.url);
                    return (
                        <a
                            key={`i${i}`}
                            href={full}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'inline-block', verticalAlign: 'middle', margin: '2px 4px' }}
                        >
                            <img
                                src={full}
                                alt="remark attachment"
                                style={{
                                    maxHeight: 80,
                                    maxWidth: 120,
                                    borderRadius: 4,
                                    border: '1px solid #E2E8F0',
                                    cursor: 'zoom-in',
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />
                        </a>
                    );
                }
                return renderTextChunk(tok.value, `c${i}`);
            })}
        </span>
    );
}
